import { QUERY_PARAM_INTERNAL_SOURCE } from 'constants/queryAndStorageKeys';
import { REGISTRATION_SUCCESS } from 'constants/routes';

import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { FORM_ERROR, createForm } from 'final-form';
import useReCAPTCHA, { moveCaptchaAttributeErrorToFormError } from 'hooks/useReCAPTCHA';
import Cookies from 'js-cookie';
import { isEmpty, pick } from 'lodash/fp';
import { getCjData } from 'modules/affiliate/storage';
import AccountRegistrationCreateMutation from 'mutations/AccountRegistrationCreateMutation.graphql';
import AccountReferrerQuery from 'queries/AccountReferrerQuery.graphql';
import React, { useEffect, useState } from 'react';
import {
	getConsentsFromFormValues,
	mtvEmailValidator,
	mtvFirstNameValidator,
	phoneValidator,
	triggerValidationMutator,
} from 'utils/form';
import { promoSourceFromQuery } from 'utils/promoUtils';

import { useGoogleAnalytics } from '@skippay/core/components/GoogleAnalytics';
import { MARKETING_EXTENDED, REGISTRATION } from '@skippay/core/constants/consentSlugs';
import { captureException } from '@skippay/core/global/errorTracking';
import useInitialCountryLocale from '@skippay/core/hooks/useInitialCountryLocale';
import { useInitialValues } from '@skippay/core/hooks/useInitialValues';
import useLocation from '@skippay/core/hooks/useLocation';
import { FormattedMessage } from '@skippay/core/i18n/intl';
import { getOriginalError, getValidationError } from '@skippay/core/utils/gqlUtils';
import { delay } from '@skippay/core/utils/promiseUtils';

export const INITIAL_VALUE_EMAIL = `e`;
export const INITIAL_VALUE_FROM = `f`;
export const INITIAL_VALUE_NAME = `n`;
export const INITIAL_VALUE_PHONE = `p`;

export const FIELD_EMAIL = `email`;
export const FIELD_GENERAL_CONSENT = REGISTRATION;
export const FIELD_MARKETING_CONSENT = MARKETING_EXTENDED;
export const FIELD_NAME = `name`;
export const FIELD_PHONE = `phone`;

const getGALinkerParam = () => {
	try {
		// eslint-disable-next-line no-underscore-dangle
		return window._ga.getAll()[0].get('linkerParam');
	} catch {
		return '';
	}
};

const trackingQueryString = () =>
	[Cookies.get('utm-parameters'), getGALinkerParam()].filter(Boolean).join('&') || undefined;

function getInternalSource(query) {
	return query[QUERY_PARAM_INTERNAL_SOURCE];
}

type LocationState =
	| {
			referrerToken?: string;
	  }
	| undefined;

type FormValuesBase = {
	[FIELD_EMAIL]: string;
	[FIELD_MARKETING_CONSENT]: typeof MARKETING_EXTENDED;
	[FIELD_NAME]: string;
	[FIELD_PHONE]: string;
};

type FormValues =
	| FormValuesBase
	| (FormValuesBase & {
			[FIELD_GENERAL_CONSENT]: typeof REGISTRATION;
	  });

function useRegistrationScreen() {
	const {
		location: { query, state },
		history,
	} = useLocation();

	const ga = useGoogleAnalytics();

	useEffect(() => {
		const referrerToken = (state as LocationState)?.referrerToken;

		ga.sendPageview({
			pTitle: 'registration',
			...(referrerToken && { referralId: referrerToken }),
		});
	}, []);

	const initialValues = useInitialValues<{
		[INITIAL_VALUE_EMAIL]?: string;
		[INITIAL_VALUE_FROM]?: string;
		[INITIAL_VALUE_NAME]?: string;
		[INITIAL_VALUE_PHONE]?: string;
	}>();

	const isInitialValueSet =
		initialValues &&
		(initialValues[INITIAL_VALUE_NAME] ||
			initialValues[INITIAL_VALUE_EMAIL] ||
			initialValues[INITIAL_VALUE_PHONE]);

	const referrerToken = (state as LocationState)?.referrerToken;
	const accountReferrerQuery = useQuery(AccountReferrerQuery, {
		skip: !referrerToken,
		variables: { code: referrerToken },
	});
	const promoSource = promoSourceFromQuery(query);
	const internalSource = getInternalSource(query);
	const { getRecaptchaToken, recaptchaNode } = useReCAPTCHA();
	const [registrationMutate, registrationResult] = useMutation(AccountRegistrationCreateMutation);

	const { country } = useInitialCountryLocale();

	const [registrationFormApi] = useState(() =>
		createForm({
			mutators: {
				triggerValidation: triggerValidationMutator,
			},
			async onSubmit(formValues: FormValues) {
				let captchaToken: string | undefined;

				try {
					captchaToken = await getRecaptchaToken();
				} catch (error) {
					captureException(new Error(`The obtaining of the reCAPTCHA token failed`), {
						extra: { originalError: error },
					});
					return {
						[FORM_ERROR]: [
							<FormattedMessage
								defaultMessage="Tato stránka je chráněna proti útoku robotů, podle níž jste vyplnili formulář podezřele rychle. Prosím, načtěte stránku znovu a pokuste se znovu zaregistrovat. Pokud potíže přetrvají, neváhejte nás kontaktovat."
								id="RegistrationScreen.error.captchaToken"
							/>,
						],
					};
				}
				const { email, name, phone } = formValues;
				const consents = getConsentsFromFormValues(formValues);
				const cjData = getCjData();
				const otherCjData = pick(['AID', 'PID', 'SID'], cjData);
				return registrationMutate({
					variables: {
						body: {
							consents,
							captchaToken,
							email,
							name,
							phone,
							promoSource,
							country,
							...(referrerToken && { referralCode: referrerToken }),
							trackingQueryString: trackingQueryString(),
							...(!isEmpty(otherCjData) && {
								extras: {
									cjAid: otherCjData.AID,
									cjPid: otherCjData.PID,
									cjSid: otherCjData.SID,
								},
							}),
						},
					},
				}).then(
					({
						data: {
							accountRegistrationCreate: { customerId },
						},
					}) => {
						const isLoginSource = internalSource === 'login';

						ga.sendEvent(
							{
								eAct: 'Registration',
								eCat: 'E-shop',
								eLab: 'Created',
							},
							{
								mktConsent: consents.includes(MARKETING_EXTENDED),
								userId: customerId,
								rStarted: isLoginSource ? 'login' : 'direct',
							}
						);
						history?.push({ pathname: REGISTRATION_SUCCESS, query, state: { email } });
						// wait some more time until redirection finishes
						return delay(10000);
					},
					(error) =>
						moveCaptchaAttributeErrorToFormError(getValidationError(error)) || Promise.reject(error)
				);
			},
		})
	);

	const apolloClient = useApolloClient();
	const firstNameOnBlurValidator = mtvFirstNameValidator(true, apolloClient);
	const emailOnBlurValidator = mtvEmailValidator;

	return {
		accountReferrerQuery,
		firstNameOnBlurValidator,
		emailOnBlurValidator,
		initialValues,
		isInitialValueSet,
		phoneOnBlurValidator: phoneValidator,
		recaptchaNode,
		registrationError: getOriginalError(registrationResult.error),
		registrationFormApi,
	};
}

export default useRegistrationScreen;
